import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { tripOriginState } from '../../utils/tripBackTarget';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { Mic, Plus, Search, Loader2, Mail, Building2, Calendar, MoreVertical, Pencil, Trash2, Send, UserCheck, UserX } from 'lucide-react';
import AddSpeakerForm from './AddSpeakerForm';
import CreateSpeakerProgramModal from './CreateSpeakerProgramModal';

/**
 * Speaker management page (speaker_bureau paradigm). Org-wide shared list:
 * every meeting planner sees and edits all speakers. Lightweight — no approval
 * queue, no per-planner assignment.
 */
const SpeakerManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editSpeaker, setEditSpeaker] = useState(null);
  const [showProgram, setShowProgram] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [portalStatus, setPortalStatus] = useState({}); // speakerId -> { status, ... }
  const [portalBusy, setPortalBusy] = useState(null);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/speakers`, { headers, params: { q: q || undefined, limit: 500 } });
      setSpeakers(res.data || []);
    } catch {
      setSpeakers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Guide deep links open the intended creation dialog immediately. Consume
  // the action after opening so it behaves like a one-time command.
  useEffect(() => {
    const action = searchParams.get('action');
    if (action !== 'new-speaker' && action !== 'new-program') return;

    if (action === 'new-speaker') setShowAdd(true);
    if (action === 'new-program') setShowProgram(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const t = setTimeout(() => load(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query, load]);

  const handleDeactivate = async (speaker) => {
    setMenuFor(null);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/speakers/${speaker.id}`, { headers });
      load(query.trim());
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not deactivate the speaker');
    }
  };

  const handleDelete = async (speaker) => {
    setMenuFor(null);
    const name = speaker.full_name || `${speaker.first_name || ''} ${speaker.last_name || ''}`.trim() || 'this speaker';
    if (!window.confirm(`Permanently delete ${name}? They will be removed from every program, and any portal login is revoked. This cannot be undone.`)) return;
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/speakers/${speaker.id}`, { headers, params: { hard: true, detach: true } });
      load(query.trim());
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not delete the speaker');
    }
  };

  // Speaker portal onboarding (view-only external login for the speaker)
  const fetchPortalStatus = useCallback(async (speakerId) => {
    try {
      const res = await axios.get(`${getApiBase()}/speakers/${speakerId}/portal-status`, { headers: getAuthHeaders() });
      setPortalStatus((prev) => ({ ...prev, [speakerId]: res.data }));
    } catch {
      /* non-admins can't manage invites; leave menu without portal items */
    }
  }, []);

  const toggleMenu = (s) => {
    const next = menuFor === s.id ? null : s.id;
    setMenuFor(next);
    if (next) fetchPortalStatus(s.id);
  };

  const handlePortalInvite = async (speaker) => {
    setPortalBusy(speaker.id);
    try {
      await axios.post(`${getApiBase()}/speakers/${speaker.id}/portal-invite`, {}, { headers: getAuthHeaders() });
      await fetchPortalStatus(speaker.id);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not send the portal invite');
    } finally {
      setPortalBusy(null);
    }
  };

  const handlePortalRevoke = async (speaker) => {
    setPortalBusy(speaker.id);
    try {
      await axios.delete(`${getApiBase()}/speakers/${speaker.id}/portal-invite`, { headers: getAuthHeaders() });
      await fetchPortalStatus(speaker.id);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not revoke the portal invite');
    } finally {
      setPortalBusy(null);
    }
  };

  // Remove the speaker's portal login entirely (active or deactivated account).
  // Returns the speaker to "not invited" so they can be re-invited or deleted.
  const handlePortalRemove = async (speaker) => {
    if (!window.confirm('Remove this speaker\'s portal login? They will no longer be able to sign in. You can re-invite them later.')) return;
    setPortalBusy(speaker.id);
    try {
      await axios.delete(`${getApiBase()}/speakers/${speaker.id}/portal-account`, { headers: getAuthHeaders() });
      await fetchPortalStatus(speaker.id);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not remove portal access');
    } finally {
      setPortalBusy(null);
    }
  };

  const displayName = (s) => s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unnamed speaker';

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-500 text-white">
            <Mic className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Speakers</h1>
            <p className="text-sm text-slate-500">Shared across your meeting-planner team</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowProgram(true)}
            className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
          >
            <Calendar className="h-4 w-4" /> New Program
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
          >
            <Plus className="h-4 w-4" /> Add Speaker
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search speakers by name, email, affiliation..."
          className="h-11 w-full rounded-xl border border-slate-200 pl-10 pr-3 text-sm"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : speakers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 py-16 text-center">
          <Mic className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-500">No speakers yet.</p>
          <button onClick={() => setShowAdd(true)} className="mt-3 text-sm font-semibold text-rose-600">
            Add your first speaker
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {speakers.map((s) => (
            <div key={s.id} className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-800">
                    {s.honorific ? `${s.honorific} ` : ''}{displayName(s)}
                    {s.credentials ? <span className="ml-1 text-xs font-normal text-slate-400">{s.credentials}</span> : null}
                  </p>
                  {s.specialty && <p className="truncate text-sm text-slate-500">{s.specialty}</p>}
                </div>
                <div className="relative">
                  <button onClick={() => toggleMenu(s)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {menuFor === s.id && (
                    <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                      <button onClick={() => { setEditSpeaker(s); setMenuFor(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      {/* Portal onboarding (view-only speaker login) */}
                      {(() => {
                        const ps = portalStatus[s.id];
                        if (!ps) return null;
                        const busy = portalBusy === s.id;
                        if (ps.status === 'active') {
                          return (
                            <>
                              <div className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-600">
                                <UserCheck className="h-3.5 w-3.5" /> Portal active
                              </div>
                              <button onClick={() => handlePortalRemove(s)} disabled={busy} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                                <UserX className="h-3.5 w-3.5" /> {busy ? 'Removing…' : 'Remove portal access'}
                              </button>
                            </>
                          );
                        }
                        if (ps.status === 'deactivated') {
                          return (
                            <>
                              <div className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400">
                                <UserX className="h-3.5 w-3.5" /> Portal deactivated
                              </div>
                              <button onClick={() => handlePortalRemove(s)} disabled={busy} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                                <UserX className="h-3.5 w-3.5" /> {busy ? 'Removing…' : 'Remove portal access'}
                              </button>
                            </>
                          );
                        }
                        if (ps.status === 'invited') {
                          return (
                            <>
                              <button onClick={() => handlePortalInvite(s)} disabled={busy} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50">
                                <Send className="h-3.5 w-3.5" /> {busy ? 'Sending…' : 'Resend portal invite'}
                              </button>
                              <button onClick={() => handlePortalRevoke(s)} disabled={busy} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                                <UserX className="h-3.5 w-3.5" /> Revoke portal invite
                              </button>
                            </>
                          );
                        }
                        return (
                          <button
                            onClick={() => handlePortalInvite(s)}
                            disabled={busy || !s.email}
                            title={s.email ? 'Email the speaker a view-only portal invite' : 'Add an email to this speaker first'}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50"
                          >
                            <Send className="h-3.5 w-3.5" /> {busy ? 'Sending…' : 'Invite to portal'}
                          </button>
                        );
                      })()}
                      <button onClick={() => handleDeactivate(s)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" /> Deactivate
                      </button>
                      <button onClick={() => handleDelete(s)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50" title="Org admins only — removes the speaker from every program">
                        <Trash2 className="h-3.5 w-3.5" /> Delete permanently
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-500">
                {s.email && <p className="flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5" /> {s.email}</p>}
                {s.affiliation && <p className="flex items-center gap-1.5 truncate"><Building2 className="h-3.5 w-3.5" /> {s.affiliation}</p>}
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-xs text-slate-400">
                  {s.engagement_count || 0} program{(s.engagement_count || 0) === 1 ? '' : 's'}
                </span>
                <button
                  onClick={() => { setShowProgram(true); }}
                  className="text-xs font-semibold text-rose-600"
                >
                  + Program
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddSpeakerForm onClose={() => setShowAdd(false)} onCreated={() => load(query.trim())} />
      )}
      {editSpeaker && (
        <AddSpeakerForm
          speakerId={editSpeaker.id}
          initial={editSpeaker}
          onClose={() => setEditSpeaker(null)}
          onCreated={() => load(query.trim())}
        />
      )}
      {showProgram && (
        <CreateSpeakerProgramModal
          createMeetingComponent
          onClose={() => setShowProgram(false)}
          onCreated={(data) => {
            setShowProgram(false);
            load(query.trim());
            if (data?.trip?.id) navigate(`/Trip/${data.trip.id}`, { state: tripOriginState(location) });
          }}
        />
      )}
    </div>
  );
};

export default SpeakerManagement;
